/** Market Intel — v1 deal-engine.js intelView() aggregations, transcribed as
 * pure functions: headline outcome tiles, per-county table, per-keyword-tier
 * table, hottest-leads list. All computed from data v2 already holds
 * (leads + rec_org_settings OUTCOME_LOG + the Deal Engine). */
import type { Lead } from "../../types";
import {
  DE_SOFT_COUNTIES, deCuts, deDom, deKeywords, deScore, deSuggestOffer, narrowOutcome, outcomeFor,
  type DeOffer,
} from "../leads/dealEngine";

/** v1 deMedian: sorted middle; even length → rounded mean of the middles. */
export function deMedian(arr: number[]): number {
  if (!arr.length) return 0;
  const a = [...arr].sort((x, y) => x - y);
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m]! : Math.round((a[m - 1]! + a[m]!) / 2);
}

export interface IntelHeadline {
  tracked: number;
  pending: number; // went pending, not sold yet
  bom: number;
  sold: number;
  medianCutPct: number | null; // median cut size % across every logged cut
}

/** v1 intelView headline tiles over the whole OUTCOME_LOG. */
export function intelHeadline(outcomeLog: unknown): IntelHeadline {
  if (outcomeLog === null || typeof outcomeLog !== "object" || Array.isArray(outcomeLog)) {
    return { tracked: 0, pending: 0, bom: 0, sold: 0, medianCutPct: null };
  }
  let tracked = 0, pending = 0, bom = 0, sold = 0;
  const cutSizes: number[] = [];
  for (const key of Object.keys(outcomeLog as Record<string, unknown>)) {
    if (key.startsWith("__")) continue; // scratch keys (e.g. __roi_calc__ style)
    const o = narrowOutcome((outcomeLog as Record<string, unknown>)[key]);
    if (!o) continue;
    tracked++;
    if (o.pendingAt && !o.soldAt) pending++;
    if (o.bomAt) bom++;
    if (o.soldAt) sold++;
    for (const c of o.cuts) {
      if (c.from && c.to && c.from > c.to) cutSizes.push(((c.from - c.to) / c.from) * 100);
    }
  }
  return {
    tracked, pending, bom, sold,
    medianCutPct: cutSizes.length ? Math.round(deMedian(cutSizes) * 10) / 10 : null,
  };
}

export interface CountyRow {
  county: string;
  soft: boolean;
  n: number;
  medPrice: number; // 0 = unknown
  medDom: number;
  cutPct: number; // % of listings with a cut
  pending: number;
  bom: number;
  sold: number;
  soldToListPct: number | null; // avg sold/list ratio ×100
}

const countyName = (c: string | null): string => (c ?? "Unknown").replace(/ county/i, "").trim() || "Unknown";

/** v1 per-county aggregates, sorted by listing count. */
export function intelByCounty(leads: Lead[], outcomeLog: unknown, now: number): CountyRow[] {
  const by = new Map<string, { n: number; prices: number[]; doms: number[]; cut: number; pending: number; bom: number; sold: number; ratios: number[] }>();
  for (const p of leads) {
    const c = countyName(p.county);
    let b = by.get(c);
    if (!b) {
      b = { n: 0, prices: [], doms: [], cut: 0, pending: 0, bom: 0, sold: 0, ratios: [] };
      by.set(c, b);
    }
    b.n++;
    if (p.price) b.prices.push(p.price);
    const ol = outcomeFor(outcomeLog, p.address);
    const d = deDom(p, now, ol);
    if (d) b.doms.push(d);
    if (deCuts(p)) b.cut++;
    if (ol) {
      if (ol.pendingAt) b.pending++;
      if (ol.bomAt) b.bom++;
      if (ol.soldAt) {
        b.sold++;
        if (ol.soldPrice && ol.listPrice) b.ratios.push(ol.soldPrice / ol.listPrice);
      }
    }
  }
  return [...by.entries()]
    .sort((a, b) => b[1].n - a[1].n)
    .map(([county, b]) => ({
      county,
      soft: DE_SOFT_COUNTIES.some((s) => county.toLowerCase().includes(s)),
      n: b.n,
      medPrice: deMedian(b.prices),
      medDom: deMedian(b.doms),
      cutPct: b.n ? Math.round((b.cut / b.n) * 100) : 0,
      pending: b.pending,
      bom: b.bom,
      sold: b.sold,
      soldToListPct: b.ratios.length ? Math.round((b.ratios.reduce((x, y) => x + y, 0) / b.ratios.length) * 100) : null,
    }));
}

export interface TierRow {
  tier: string;
  n: number;
  cutPct: number;
  medDom: number;
  sold: number;
  soldToListPct: number | null;
}

/** v1 per-keyword-tier aggregates. */
export function intelByTier(leads: Lead[], outcomeLog: unknown, now: number): TierRow[] {
  const by = new Map<string, { n: number; cut: number; doms: number[]; sold: number; ratios: number[] }>();
  for (const p of leads) {
    const kw = deKeywords(p.desc, null);
    if (!kw.tier) continue;
    let b = by.get(kw.tier.name);
    if (!b) {
      b = { n: 0, cut: 0, doms: [], sold: 0, ratios: [] };
      by.set(kw.tier.name, b);
    }
    b.n++;
    if (deCuts(p)) b.cut++;
    const ol = outcomeFor(outcomeLog, p.address);
    const d = deDom(p, now, ol);
    if (d) b.doms.push(d);
    if (ol?.soldAt) {
      b.sold++;
      if (ol.soldPrice && ol.listPrice) b.ratios.push(ol.soldPrice / ol.listPrice);
    }
  }
  return [...by.entries()].map(([tier, b]) => ({
    tier,
    n: b.n,
    cutPct: b.n ? Math.round((b.cut / b.n) * 100) : 0,
    medDom: deMedian(b.doms),
    sold: b.sold,
    soldToListPct: b.ratios.length ? Math.round((b.ratios.reduce((x, y) => x + y, 0) / b.ratios.length) * 100) : null,
  }));
}

export interface HotLead {
  lead: Lead;
  score: number;
  tierName: string | null;
  dom: number;
  cuts: number;
  offer: DeOffer | null;
}

/** v1 hottest list: score ≥40, top 12 by score. */
export function intelHot(
  leads: Lead[],
  edits: Map<string, Record<string, unknown>>,
  keyOf: (address: string) => string,
  outcomeLog: unknown,
  now: number,
): HotLead[] {
  const out: HotLead[] = [];
  for (const p of leads) {
    const ol = outcomeFor(outcomeLog, p.address);
    const s = deScore(p, null, now, ol);
    if (s < 40) continue;
    const kw = deKeywords(p.desc, null);
    out.push({
      lead: p,
      score: s,
      tierName: kw.tier?.name ?? null,
      dom: deDom(p, now, ol),
      cuts: deCuts(p),
      offer: deSuggestOffer(p, edits.get(keyOf(p.address)), null, now, ol),
    });
  }
  return out.sort((a, b) => b.score - a.score).slice(0, 12);
}
